import { useEffect, useState } from "react";
import { AlertTriangle, Megaphone, Plus, Pin, PinOff, Trash2, Edit3, Eye, EyeOff, X } from "lucide-react";
import { api, apiError, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * Full announcement whiteboard — one board for the whole company.
 * - All authenticated users can read (client sees only client_visible posts).
 * - Owner / HO Admin / PM can post, edit, pin, expire, delete.
 * - Emergency posts sort first and render red.
 */
export default function WhiteboardPage() {
    const { user } = useAuth();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showComposer, setShowComposer] = useState(false);
    const [editingPost, setEditingPost] = useState(null);
    const canPost = ["OWNER", "HEAD", "PROJECT_MANAGER"].includes(user?.effective_role);
    const canSeeAll = ["OWNER", "HEAD"].includes(user?.effective_role);
    const [showAll, setShowAll] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const path = showAll && canSeeAll ? "/whiteboard/all" : "/whiteboard/active";
            const { data } = await api.get(path);
            setPosts(data);
            // Mark all visible posts as read on load. Backend upsert makes this idempotent.
            // Bell listens for the `whiteboard-read` window event to refresh its badge.
            if (Array.isArray(data) && data.length) {
                try {
                    await Promise.all(
                        data
                            .filter((p) => !p.deleted_at)
                            .map((p) => api.post(`/whiteboard/${p.id}/read`).catch(() => null)),
                    );
                    window.dispatchEvent(new Event("whiteboard-read"));
                } catch { /* no-op */ }
            }
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, [showAll]);

    async function togglePin(p) {
        try { await api.patch(`/whiteboard/${p.id}`, { pinned: !p.pinned }); load(); }
        catch (e) { toast.error(apiError(e)); }
    }
    async function remove(p) {
        if (!window.confirm(`Delete "${p.title}"? This can only be reversed by HO Admin.`)) return;
        try { await api.delete(`/whiteboard/${p.id}`); load(); toast.success("Deleted."); }
        catch (e) { toast.error(apiError(e)); }
    }
    async function markRead(p) {
        try { await api.post(`/whiteboard/${p.id}/read`); } catch { /* no-op */ }
    }

    return (
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-6" data-testid="whiteboard-page">
            <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#1E3355] text-white flex items-center justify-center"><Megaphone className="w-5 h-5" /></div>
                    <div>
                        <div className="text-lg font-semibold text-[#1E3355]">Announcement Whiteboard</div>
                        <div className="text-xs text-gray-600">Shared notice board — one for the whole company.</div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {canSeeAll && (
                        <button
                            onClick={() => setShowAll((v) => !v)}
                            className="inline-flex items-center gap-1 text-xs text-[#1E3355] px-2 py-1 rounded border border-gray-200 hover:bg-gray-50"
                            data-testid="wb-toggle-all"
                        >
                            {showAll ? <><EyeOff className="w-3.5 h-3.5" /> Hide archived</> : <><Eye className="w-3.5 h-3.5" /> Show archived</>}
                        </button>
                    )}
                    {canPost && (
                        <button
                            onClick={() => { setEditingPost(null); setShowComposer(true); }}
                            className="inline-flex items-center gap-1 bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-3 py-1.5 rounded-md"
                            data-testid="wb-new-post"
                        ><Plus className="w-4 h-4" /> New post</button>
                    )}
                </div>
            </div>

            {loading && <div className="text-sm text-gray-500 py-10 text-center">Loading…</div>}
            {!loading && posts.length === 0 && (
                <div className="rounded-xl bg-white border border-gray-200 p-8 text-center text-sm text-gray-500" data-testid="wb-empty">
                    Nothing on the board yet.
                </div>
            )}

            <div className="space-y-3">
                {posts.map((p) => (
                    <div
                        key={p.id}
                        onMouseEnter={() => markRead(p)}
                        className={`rounded-xl border overflow-hidden ${p.kind === "EMERGENCY" ? "border-[#E02B2B] bg-red-50/40" : "border-gray-200 bg-white"} ${p.deleted_at ? "opacity-60" : ""}`}
                        data-testid={`wb-post-${p.id}`}
                    >
                        <div className={`px-4 py-3 flex items-start gap-3 ${p.kind === "EMERGENCY" ? "bg-[#E02B2B] text-white" : ""}`}>
                            {p.kind === "EMERGENCY" ? <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" /> : <Megaphone className="w-4 h-4 shrink-0 mt-0.5 text-[#1E3355]" />}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`text-[10px] font-bold uppercase tracking-widest ${p.kind === "EMERGENCY" ? "" : "text-[#1E3355]"}`}>
                                        {p.kind === "EMERGENCY" ? "Emergency" : "Announcement"}
                                    </span>
                                    {p.pinned && <span className={`text-[10px] uppercase tracking-widest ${p.kind === "EMERGENCY" ? "text-white/85" : "text-amber-700"}`}><Pin className="w-3 h-3 inline" /> Pinned</span>}
                                    {p.client_visible && <span className={`text-[10px] uppercase tracking-widest ${p.kind === "EMERGENCY" ? "text-white/85" : "text-emerald-700"}`}>Client-visible</span>}
                                    {p.expires_at && <span className={`text-[10px] ${p.kind === "EMERGENCY" ? "text-white/80" : "text-gray-500"}`}>expires {fmtDate(p.expires_at)}</span>}
                                    {p.deleted_at && <span className="text-[10px] text-red-700 font-semibold">DELETED</span>}
                                </div>
                                <div className={`font-semibold text-base mt-1 ${p.kind === "EMERGENCY" ? "" : "text-[#1E3355]"}`}>{p.title}</div>
                            </div>
                        </div>
                        <div className="px-4 py-3">
                            <div className="text-sm text-gray-800 whitespace-pre-wrap">{p.body}</div>
                            <div className="flex items-center justify-between mt-3 text-[11px] text-gray-500 border-t border-gray-100 pt-2">
                                <div>Posted by <span className="font-semibold text-[#1E3355]">{p.posted_by_name}</span> · {p.posted_at?.slice(0, 16).replace("T", " ")} IST</div>
                                {canPost && !p.deleted_at && (
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => togglePin(p)} className="text-[#1E3355] hover:text-black" data-testid={`wb-pin-${p.id}`}>{p.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}</button>
                                        <button onClick={() => { setEditingPost(p); setShowComposer(true); }} className="text-[#1E3355] hover:text-black" data-testid={`wb-edit-${p.id}`}><Edit3 className="w-3.5 h-3.5" /></button>
                                        <button onClick={() => remove(p)} className="text-[#E02B2B] hover:text-red-800" data-testid={`wb-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {showComposer && (
                <Composer
                    initial={editingPost}
                    onClose={() => { setShowComposer(false); setEditingPost(null); }}
                    onSaved={() => { setShowComposer(false); setEditingPost(null); load(); }}
                />
            )}
        </div>
    );
}

function Composer({ initial, onClose, onSaved }) {
    const isEdit = !!initial;
    const [title, setTitle] = useState(initial?.title || "");
    const [body, setBody] = useState(initial?.body || "");
    const [kind, setKind] = useState(initial?.kind || "NORMAL");
    const [expiresAt, setExpiresAt] = useState(initial?.expires_at?.slice(0, 10) || "");
    const [pinned, setPinned] = useState(initial?.pinned || false);
    const [clientVisible, setClientVisible] = useState(initial?.client_visible || false);
    const [busy, setBusy] = useState(false);

    async function submit() {
        if (!title.trim() || !body.trim()) { toast.error("Title and body are required."); return; }
        setBusy(true);
        try {
            const payload = {
                title: title.trim(), body: body.trim(), kind,
                expires_at: expiresAt ? `${expiresAt}T23:59:59+05:30` : null,
                pinned, client_visible: clientVisible,
            };
            if (isEdit) {
                await api.patch(`/whiteboard/${initial.id}`, payload);
                toast.success("Post updated.");
            } else {
                await api.post("/whiteboard", payload);
                toast.success(kind === "EMERGENCY" ? "Emergency posted — HO/PM notified." : "Announcement posted.");
            }
            onSaved();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    }
    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="wb-composer">
            <div className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
                <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                    <div className="font-display font-semibold text-[#1E3355]">{isEdit ? "Edit announcement" : "New announcement"}</div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-4 space-y-3 overflow-y-auto">
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Type</label>
                        <div className="flex gap-2 mt-1">
                            <button onClick={() => setKind("NORMAL")} className={`flex-1 rounded-md border px-3 py-2 text-sm font-semibold ${kind === "NORMAL" ? "border-[#1E3355] bg-[#F4F6F9] text-[#1E3355]" : "border-gray-200 text-gray-600"}`} data-testid="wb-kind-normal">Normal</button>
                            <button onClick={() => setKind("EMERGENCY")} className={`flex-1 rounded-md border px-3 py-2 text-sm font-semibold ${kind === "EMERGENCY" ? "border-[#E02B2B] bg-red-50 text-[#E02B2B]" : "border-gray-200 text-gray-600"}`} data-testid="wb-kind-emergency">Emergency</button>
                        </div>
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Title</label>
                        <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full rounded border border-gray-300 px-3 py-2 text-sm mt-1" placeholder="Short, clear headline" data-testid="wb-title" />
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Body</label>
                        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} className="w-full rounded border border-gray-300 px-3 py-2 text-sm mt-1" placeholder="What everyone needs to know…" data-testid="wb-body" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Expires (optional)</label>
                            <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className="w-full rounded border border-gray-300 px-3 py-2 text-sm mt-1" data-testid="wb-expiry" />
                        </div>
                        <div className="flex flex-col justify-end gap-2 pb-1">
                            <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                                <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} data-testid="wb-pinned" /> Pin to top
                            </label>
                            <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                                <input type="checkbox" checked={clientVisible} onChange={(e) => setClientVisible(e.target.checked)} data-testid="wb-client-visible" /> Also show to clients
                            </label>
                        </div>
                    </div>
                </div>
                <div className="p-4 border-t border-gray-100 flex items-center justify-end gap-2">
                    <button onClick={onClose} className="text-sm text-gray-600 hover:text-gray-800 px-3 py-2">Cancel</button>
                    <button onClick={submit} disabled={busy || !title.trim() || !body.trim()} className={`inline-flex items-center gap-1 text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-40 ${kind === "EMERGENCY" ? "bg-[#E02B2B] hover:bg-red-700" : "bg-[#1E3355] hover:bg-[#15243C]"}`} data-testid="wb-submit">
                        {isEdit ? "Save" : (kind === "EMERGENCY" ? "Post emergency" : "Post announcement")}
                    </button>
                </div>
            </div>
        </div>
    );
}
